import { Popover as PopoverPrimitive } from '@base-ui/react/popover';
import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { Container } from './container.tsx';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogHeader,
    DialogIconClose,
    DialogTitle,
    DialogTrigger,
    type DialogHeaderProps,
    type DialogSnapMode,
} from './dialog.tsx';

export { DialogBody as PopoverBody, DialogFooter as PopoverFooter } from './dialog.tsx';
export type {
    DialogBodyProps as PopoverBodyProps,
    DialogFooterProps as PopoverFooterProps,
} from './dialog.tsx';

export type PopoverProps = {
    children?: ReactNode;
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    modal?: PopoverPrimitive.Root.Props['modal'];
};

export function Popover({
    children,
    open,
    defaultOpen = false,
    onOpenChange,
    modal,
}: PopoverProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });

    if (!isDesktop) {
        return (
            <Dialog open={isOpen} onOpenChange={setOpen}>
                {children}
            </Dialog>
        );
    }

    return (
        <PopoverPrimitive.Root open={isOpen} onOpenChange={setOpen} modal={modal}>
            {children}
        </PopoverPrimitive.Root>
    );
}

export type PopoverTriggerProps = ComponentPropsWithRef<'button'> &
    ButtonVariantProps & {
        render?: ReactElement;
    };

export function PopoverTrigger({ render, children, ...props }: PopoverTriggerProps): ReactElement {
    const isDesktop = useBreakpoint('sm');

    if (!isDesktop) {
        return (
            <DialogTrigger render={render} {...props}>
                {children}
            </DialogTrigger>
        );
    }

    return (
        <PopoverPrimitive.Trigger data-slot="popover-trigger" render={render ?? <Button {...props} />}>
            {children}
        </PopoverPrimitive.Trigger>
    );
}

export type PopoverContentProps = Omit<ComponentPropsWithRef<'div'>, 'children'> &
    Pick<PopoverPrimitive.Popup.Props, 'children' | 'initialFocus' | 'finalFocus'> &
    Pick<
        PopoverPrimitive.Positioner.Props,
        'align' | 'alignOffset' | 'anchor' | 'collisionPadding' | 'side' | 'sideOffset'
    > & {
        snapMode?: DialogSnapMode;
    };

export function PopoverContent({
    align = 'start',
    alignOffset = 0,
    anchor,
    children,
    className,
    collisionPadding = 8,
    side = 'bottom',
    sideOffset = 6,
    snapMode,
    ...props
}: PopoverContentProps): ReactElement {
    const isDesktop = useBreakpoint('sm');

    if (!isDesktop) {
        return (
            <DialogContent variant="picker" snapMode={snapMode} className={className} {...props}>
                {children}
            </DialogContent>
        );
    }

    return (
        <PopoverPrimitive.Portal>
            <PopoverPrimitive.Backdrop data-slot="popover-overlay" className="fixed inset-0 z-40" />
            <PopoverPrimitive.Positioner
                data-slot="popover-positioner"
                className="isolate z-50"
                align={align}
                alignOffset={alignOffset}
                anchor={anchor}
                collisionPadding={collisionPadding}
                side={side}
                sideOffset={sideOffset}
            >
                <Container
                    level={1}
                    render={
                        <PopoverPrimitive.Popup
                            data-slot="popover-content"
                            className={cn(
                                'relative flex max-h-(--available-height) min-h-0 flex-col overflow-hidden',
                                'origin-(--transform-origin) rounded-default border border-line-weak',
                                'bg-container text-content shadow-lg outline-none',
                                'transition-[transform,scale,opacity] ease-standard duration-interactive',
                                'data-ending-style:duration-state',
                                'data-starting-style:scale-96 data-starting-style:opacity-0',
                                'data-ending-style:scale-96 data-ending-style:opacity-0',
                                className,
                            )}
                            {...props}
                        />
                    }
                >
                    {children}
                </Container>
            </PopoverPrimitive.Positioner>
        </PopoverPrimitive.Portal>
    );
}

export type PopoverHeaderProps = DialogHeaderProps;

export function PopoverHeader({ ...props }: PopoverHeaderProps): ReactElement | null {
    const isDesktop = useBreakpoint('sm');

    if (isDesktop) {
        return null;
    }

    return <DialogHeader {...props} />;
}

export type PopoverTitleProps = ComponentPropsWithRef<'h2'>;

export function PopoverTitle({ ...props }: PopoverTitleProps): ReactElement | null {
    const isDesktop = useBreakpoint('sm');

    if (isDesktop) {
        return null;
    }

    return <DialogTitle {...props} />;
}

export type PopoverCloseProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

export function PopoverClose({ ...props }: PopoverCloseProps): ReactElement | null {
    const isDesktop = useBreakpoint('sm');

    if (isDesktop) {
        return null;
    }

    return <DialogClose {...props} />;
}

export type PopoverIconCloseProps = PopoverCloseProps;

export function PopoverIconClose({ ...props }: PopoverIconCloseProps): ReactElement | null {
    const isDesktop = useBreakpoint('sm');

    if (isDesktop) {
        return null;
    }

    return <DialogIconClose {...props} />;
}
